import type { ReactNode } from 'react'
import { cx } from '../../ui'

/**
 * One part of a Settings tab: what it is on the left, its state and controls on the right.
 *
 * Settings was a card inside the page's card for every part, each a narrow column of label, value and
 * paragraph down the left of a wide box, so a tab read as one long wall (owner, 2026-10-11: "we need to
 * enhance also the UI experience for settings"). Now each part is a row: its name and the sentence
 * saying what it is for sit beside what it holds, the parts are divided by a rule rather than boxed, and
 * on a narrow screen the two stack. The heading stays an h2, as the card's was, so a screen reader still
 * moves between the parts by heading.
 */
export function SettingsSection({
  title,
  description,
  tone,
  children,
}: {
  title: ReactNode
  description?: ReactNode
  /** The one destructive part (closing the workspace): its controls sit in a rose-ruled box. */
  tone?: 'danger'
  children?: ReactNode
}) {
  return (
    <section className={cx('settings-section', tone === 'danger' && 'settings-section-danger')}>
      <div className="settings-section-head">
        <h2 className="settings-section-title">{title}</h2>
        {description ? <div className="settings-section-desc">{description}</div> : null}
      </div>
      <div className="settings-section-body">{children}</div>
    </section>
  )
}

/**
 * A longer explanation one click away. The words stay on the page, unchanged, folded under a plain
 * label: they are what a careful reader checks, and what made every tab look crowded to everyone else.
 * A native <details>, so it opens from the keyboard and a screen reader announces it as expandable.
 */
export function MoreAbout({ label, children }: { label: string; children: ReactNode }) {
  return (
    <details className="settings-more">
      <summary>{label}</summary>
      <div className="settings-more-body">{children}</div>
    </details>
  )
}
